import React, { useState, useEffect, ChangeEvent } from 'react';
import { Table, Button, Input, Form, message, Space, Row, Col, Modal } from 'antd';
import apiClient from '../services/api';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { fetchWithLoading } from '../utils/fetchHelper';
import { confirmDelete } from '../utils/confirmDelete';
import { accentInsensitiveIncludes } from '../utils/accentUtils';

interface Company {
  id: number;
  name: string;
  company_code?: string;
  address?: string;
  tax_code?: string;
}

const CompanyPage = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [modalVisible, setModalVisible] = useState(false);

  const fetchCompanies = async () => {
    await fetchWithLoading(
      () => apiClient.getCompanies(),
      setLoading,
      (data) => setCompanies(data as Company[]),
      'Không thể tải danh sách công ty'
    );
  };

  useEffect(() => { fetchCompanies(); }, []);

  if (!user || user.role !== 'super_admin') {
    return null; // hoặc return <div>Bạn không có quyền truy cập trang này.</div>
  }

  const handleSubmit = async (values: any) => {
    try {
      if (editingCompany) {
        await apiClient.updateCompany(editingCompany.id, values);
        message.success('Cập nhật công ty thành công!');
      } else {
        await apiClient.createCompany(values);
        message.success('Tạo công ty thành công!');
      }
      form.resetFields();
      setEditingCompany(null);
      setModalVisible(false);
      fetchCompanies();
    } catch (err: any) {
      const msg = err?.response?.data?.message || (editingCompany ? 'Cập nhật thất bại!' : 'Tạo công ty thất bại!');
      message.error(msg);
    }
  };

  const handleEdit = (record: Company) => {
    setEditingCompany(record);
    form.setFieldsValue({ name: record.name, company_code: record.company_code, address: record.address });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteCompany(id);
      message.success('Đã xóa công ty');
      fetchCompanies();
    } catch {
      message.error('Xóa công ty thất bại!');
    }
  };

  const handleDeleteClick = (record: Company) => {
    confirmDelete('Bạn có chắc muốn xóa công ty này?', () => handleDelete(record.id));
  };

  const filteredCompanies = companies.filter((c) =>
    accentInsensitiveIncludes(c.name, searchValue) ||
    accentInsensitiveIncludes(c.company_code || '', searchValue) ||
    accentInsensitiveIncludes(c.address || '', searchValue)
  );
  const columns = [
    { title: 'Tên công ty', dataIndex: 'name', key: 'name' },
    { title: 'Mã công ty', dataIndex: 'company_code', key: 'company_code' },
    { title: 'Địa chỉ', dataIndex: 'address', key: 'address' },
    {
      title: 'Hành động', key: 'action', width: 110,
      render: (_: any, record: Company) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteClick(record)} />
        </Space>
      )
    }
  ];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16, alignItems: 'center' }}>
        <Col xs={24} md={20}>
          <Input
            className="ams-input"
            placeholder="Tìm kiếm tên công ty"
            value={searchValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            allowClear
            size="large"
            style={{ width: '100%', borderRadius: 8, height: 40 }}
          />
        </Col>
        <Col xs={24} md={4}>
          <Button className="ams-btn" type="primary" size="large" icon={<EditOutlined />} style={{ borderRadius: 8, height: 40, width: '100%' }} onClick={() => { setEditingCompany(null); form.resetFields(); setModalVisible(true); }}>Thêm mới</Button>
        </Col>
      </Row>
      <Modal
        open={modalVisible}
        title={editingCompany ? 'Cập nhật công ty' : 'Tạo mới công ty'}
        onCancel={() => { setModalVisible(false); setEditingCompany(null); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Tên công ty" rules={[{ required: true, message: 'Nhập tên công ty!' }]}>
            <Input className="ams-input" placeholder="Tên công ty" size="large" style={{ width: '100%', borderRadius: 8, height: 40 }} />
          </Form.Item>
          <Form.Item name="company_code" label="Mã công ty">
            <Input className="ams-input" placeholder="Mã công ty (viết tắt)" size="large" style={{ width: '100%', borderRadius: 8, height: 40 }} />
          </Form.Item>
          <Form.Item name="address" label="Địa chỉ">
            <Input className="ams-input" placeholder="Địa chỉ" size="large" style={{ width: '100%', borderRadius: 8, height: 40 }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button className="ams-btn" type="primary" htmlType="submit" size="large" style={{ width: '100%', borderRadius: 8, height: 40 }}>{editingCompany ? 'Cập nhật' : 'Tạo mới'}</Button>
          </Form.Item>
          {editingCompany && (
            <Form.Item style={{ marginBottom: 0 }}>
              <Button className="ams-btn" style={{ width: '100%', borderRadius: 8, height: 40 }} size="large" onClick={() => { setEditingCompany(null); form.resetFields(); setModalVisible(false); }}>Hủy</Button>
            </Form.Item>
          )}
        </Form>
      </Modal>
      <Table
        dataSource={filteredCompanies}
        rowKey="id"
        loading={loading}
        columns={columns}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default CompanyPage; 